/**
 * =============================================================================
 * Hook: useActivityPlaybook
 * Versão: 1.0.0
 *
 * Descrição:
 *   Carrega o detalhe completo de um playbook (cabeçalho + steps + bindings)
 *   via RPC `get_activity_playbook_details`, que retorna um JSONB com a
 *   estrutura: { playbook, steps[], bindings[] }.
 *
 * Release notes:
 *   v1.0.0 - Criação (Fase 1, item 1.11 do Playbook de Vendas).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useActivityPlaybook');

// ----------------------------------------------------------------------------
// Tipos
// ----------------------------------------------------------------------------
export interface ActivityPlaybookStep {
  id: string;
  tenant_id: string;
  playbook_id: string;
  sort_order: number;
  activity_type: string;
  title: string;
  description_html: string | null;
  channel: string | null;
  due_offset_value: number;
  due_offset_unit: string;
  due_anchor: string;
  is_required: boolean;
  sla_hours: number | null;
  auto_complete_on: string | null;
  /** Em modo per_phase, fase à qual o step pertence */
  phase_id: string | null;
  // Roteiro (Playbook v2.0)
  script_html: string | null;
  goal: string | null;
  success_criteria: Array<{ id: string; label: string; required: boolean }>;
  objections: Array<{ id: string; question: string; answer_html: string }>;
  script_variables_used: string[];
  created_at: string;
  updated_at: string;
}

export interface PipelinePlaybookBinding {
  id: string;
  tenant_id: string;
  workspace_id: string;
  pipeline_id: string | null;
  phase_id: string | null;
  playbook_id: string;
  trigger: string;
  cancel_previous: boolean;
  lead_source_filter: string[] | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ActivityPlaybookDetails {
  playbook: {
    id: string;
    tenant_id: string;
    workspace_id: string;
    name: string;
    description: string | null;
    color: string | null;
    icon: string | null;
    is_active: boolean;
    is_default: boolean;
    /** 'flat' (padrão legado) ou 'per_phase' */
    scope: 'flat' | 'per_phase';
    /** Obrigatório quando scope = 'per_phase' */
    pipeline_id: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
  };
  steps: ActivityPlaybookStep[];
  bindings: PipelinePlaybookBinding[];
}

// ----------------------------------------------------------------------------
// Hook
// ----------------------------------------------------------------------------
export function useActivityPlaybook(playbookId: string | null | undefined) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useQuery({
    queryKey: ['activity-playbook-details', tenantId, playbookId],
    enabled: !!tenantId && !!playbookId,
    queryFn: async (): Promise<ActivityPlaybookDetails> => {
      const { data, error } = await supabase.rpc('get_activity_playbook_details', {
        p_id: playbookId!,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao obter detalhes do playbook', error);
        throw error;
      }
      return data as unknown as ActivityPlaybookDetails;
    },
  });
}
